import {
  ArrowPathIcon,
  ClockIcon,
  ChevronDownIcon,
  CloudArrowDownIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-router';

import { BulkUploadResultKey } from '../../domain/constants';
import { BulkUploadHistory } from '../../domain/entities/bulk_upload_history';

interface BulkUploadHistoryAccordionProps {
  history: BulkUploadHistory[];
  onRefresh?: () => void;
}

export const BulkUploadHistoryAccordion: React.FC<BulkUploadHistoryAccordionProps> = ({
  history,
  onRefresh,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { t } = useTranslation(['userManagement']);

  const filteredHistory = history.filter((item) => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return true;
    return (
      item.fileName?.toLowerCase().includes(query) ||
      item.status?.toLowerCase().includes(query) ||
      item.id?.toLowerCase().includes(query)
    );
  });

  const getTranslatedStatus = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return t('statusCompleted');
      case 'processing':
        return t('statusProcessing');
      case 'failed':
        return t('statusFailed');
      case 'pending':
        return t('statusPending');
      case 'created':
        return t('statusCreated');
      default:
        return status;
    }
  };

  const downloadFileFromUrl = (fileUrl: string | undefined = '', _filename?: string) => {
    if (fileUrl) window.open(fileUrl, '_blank');
  };

  const handleRefresh = async () => {
    if (onRefresh) {
      setIsRefreshing(true);
      await onRefresh();
      setIsRefreshing(false);
    }
  };

  return (
    <div className="mb-6 rounded-lg border border-neutralLight bg-white">
      <button
        type="button"
        className="bodyXs flex w-full items-center gap-2 px-3 py-2 font-medium"
        onClick={() => setIsOpen(!isOpen)}
      >
        <ClockIcon
          className={`h-5 w-5 transition-transform duration-300 ease-in-out ${isOpen ? 'rotate-[360deg]' : ''}`}
        />
        {t('viewHistory')}
        <ChevronDownIcon
          className={`ml-auto h-5 w-5 transition-transform duration-300 ease-in-out ${isOpen ? '-scale-y-100' : ''}`}
        />
      </button>

      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out`}
        style={{ maxHeight: isOpen ? '420px' : '0px' }}
      >
        <div className="flex items-center justify-start gap-2 p-3">
          <span className="font-semibold">{t('bulkUploadHistory')}</span>
          <Form
            method="post"
            action="/user-management"
          >
            <button
              // type="submit"
              onClick={handleRefresh}
              disabled={isRefreshing}
              className={`flex h-8 w-8 items-center justify-center rounded-full text-primaryDefault duration-300 ease-in-out hover:bg-primaryExtraLight ${isRefreshing ? 'cursor-not-allowed opacity-50' : ''}`}
            >
              <ArrowPathIcon className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
          </Form>
        </div>

        <div className="px-3 pb-2">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
            <input
              type="text"
              placeholder={t('searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-9 w-full rounded-lg border border-gray-300 pl-9 pr-3 text-xs focus:border-primaryDefault focus:outline-none focus:ring-1 focus:ring-primaryExtraLight"
            />
          </div>
        </div>

        <div className="max-h-72 overflow-auto">
          <table className="w-full border-collapse">
            <thead className="sticky top-0 bg-white">
              <tr className="border-b border-neutralLight">
                <th className="px-4 py-2 text-left text-xs">{t('data')}</th>
                <th className="px-4 py-2 text-left text-xs">{t('result-jobId')}</th>
                <th className="px-4 py-2 text-left text-xs">{t('fileName')}</th>
                <th className="px-4 py-2 text-left text-xs">{t('status')}</th>
              </tr>
            </thead>
            <tbody className="bodyXs">
              {filteredHistory.map((item) => (
                <tr
                  key={item.id}
                  className="border-b border-neutralLight"
                >
                  <td className="px-4 py-2">
                    {new Date(item.createdAt || '').toLocaleDateString()}
                  </td>
                  <td
                    className="cursor-pointer whitespace-nowrap px-4 py-2 font-normal"
                    onClick={() => {
                      const resultFile = item?.result?.find(
                        (i) => i.key === BulkUploadResultKey.RESULT_FILE_URL
                      )?.value;
                      const errorFile = item?.result?.find(
                        (i) => i.key === BulkUploadResultKey.ERROR_FILE_URL
                      )?.value;
                      const downloadUrl = resultFile || errorFile || item.fileUrl;
                      downloadFileFromUrl(downloadUrl, item.id);
                    }}
                  >
                    <span className="inline-flex items-center gap-1 text-green-600 underline">
                      {t('viewResultFile')}
                      <CloudArrowDownIcon className="h-4 w-4 text-green-600" />
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2">
                    {item.fileName ? (
                      <span
                        className="inline-flex cursor-pointer items-center gap-1 underline"
                        role="button"
                        tabIndex={0}
                        onClick={() => downloadFileFromUrl(item.uploadedFileUrl, item.fileName)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            downloadFileFromUrl(item.uploadedFileUrl, item.fileName);
                          }
                        }}
                      >
                        {item.fileName}
                        <CloudArrowDownIcon className="h-4 w-4" />
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <span
                      className={`inline-block rounded-2xl px-2 py-1 text-[9px] ${
                        item.status?.toUpperCase() === 'COMPLETED'
                          ? 'bg-green-100 text-green-800'
                          : item.status?.toUpperCase() === 'FAILED'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                      }`}
                    >
                      {getTranslatedStatus(item.status || '')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
